<template>
  <WidgetChart
    v-if="!isLoading"
    title=""
    actionLabel="CREAR"
  >
    <VueDataUi
      component="VueUiDonut"
      :dataset="dataset"
      :config="config"
    />
  </WidgetChart>
</template>

<script setup>
import {
  ref,
  onMounted,
  watch,
} from 'vue';
import { VueDataUi } from 'vue-data-ui';
import useApi from '@/shared/composables/useApi';
import {
  applyConfingTexts,
  buildDateRangeQueryParams,
} from './utils';
import WidgetChart from './base/chart.vue';

const { isLoading, apiGet } = useApi();

const props = defineProps({
  filters: Object,
});

const dataset = ref([]);
const config = ref(
  applyConfingTexts({
    type: 'classic',
    responsive: true,
    theme: '',
    customPalette: [],
    useCssAnimation: true,
    useBlurOnHover: true,
    userOptions: {
      show: true,
      showOnChartHover: false,
      keepStateOnChartLeave: true,
      position: 'right',
      buttons: {
        tooltip: true,
        pdf: true,
        csv: true,
        img: true,
        table: true,
        labels: true,
        fullscreen: true,
        sort: false,
        stack: false,
        animation: false,
        annotator: false,
      },
    },
    table: {
      show: false,
      responsiveBreakpoint: 400,
      th: {
        backgroundColor: '#FFFFFFff',
        color: '#1A1A1Aff',
        outline: 'none',
      },
      td: {
        backgroundColor: '#FFFFFFff',
        color: '#1A1A1Aff',
        outline: 'none',
        roundingValue: 0,
        roundingPercentage: 0,
      },
      columnNames: {
        series: 'Series',
        value: 'Value',
        percentage: 'Percentage',
      },
    },
    style: {
      fontFamily: 'inherit',
      chart: {
        useGradient: true,
        gradientIntensity: '0',
        backgroundColor: '#FFFFFFff',
        color: '#1A1A1Aff',
        layout: {
          labels: {
            dataLabels: {
              show: true,
              useLabelSlots: false,
              hideUnderValue: 3,
              prefix: '',
              suffix: '',
            },
            value: {
              rounding: 0,
              show: true,
              formatter: null,
            },
            percentage: {
              color: '#1A1A1Aff',
              bold: true,
              fontSize: 18,
              rounding: 0,
              formatter: null,
            },
            name: {
              color: '#1A1A1Aff',
              bold: false,
              fontSize: 14,
            },
            hollow: {
              show: true,
              total: {
                show: true,
                bold: false,
                fontSize: 18,
                color: '#AAAAAAff',
                text: 'Total',
                offsetY: 0,
                value: {
                  color: '#1A1A1Aff',
                  fontSize: 18,
                  bold: true,
                  suffix: '',
                  prefix: '',
                  offsetY: 0,
                  rounding: 0,
                  formatter: null,
                },
              },
              average: {
                show: false,
                bold: false,
                fontSize: 18,
                color: '#AAAAAAff',
                text: 'Average',
                offsetY: 0,
                value: {
                  color: '#1A1A1Aff',
                  fontSize: 18,
                  bold: true,
                  suffix: '',
                  prefix: '',
                  offsetY: 0,
                  rounding: 0,
                  formatter: null,
                },
              },
            },
          },
          donut: {
            strokeWidth: '34',
            borderWidth: '2',
            useShadow: false,
            shadowColor: '#1A1A1A',
          },
        },
        comments: {
          show: true,
          showInTooltip: true,
          width: 100,
          offsetY: 0,
          offsetX: 0,
        },
        legend: {
          show: true,
          bold: false,
          backgroundColor: '#FFFFFFff',
          color: '#1A1A1Aff',
          fontSize: 16,
          roundingValue: 0,
          roundingPercentage: 0,
        },
        tooltip: {
          show: true,
          color: '#1A1A1Aff',
          backgroundColor: '#FFFFFFff',
          fontSize: 14,
          customFormat: null,
          borderRadius: 4,
          borderColor: '#e1e5e8',
          borderWidth: 1,
          backgroundOpacity: 30,
          position: 'center',
          offsetY: 24,
          showValue: true,
          showPercentage: true,
          roundingValue: 0,
          roundingPercentage: 0,
        },
        title: {
          text: 'PanelGeneral',
          color: '#1A1A1Aff',
          fontSize: 20,
          bold: true,
          textAlign: 'center',
          paddingLeft: 0,
          paddingRight: 0,
          subtitle: {
            color: '#A1A1A1ff',
            text: '',
            fontSize: 16,
            bold: false,
          },
        },
      },
    },
  }),
);

const fetchData = async () => {
  const queryParams = buildDateRangeQueryParams(props.filters);
  const result = await apiGet('/dashboard/estadoscotizacion', { params: queryParams });
  dataset.value = result.data;
};

onMounted(() => {
  fetchData();
});

watch(
  () => props.filters,
  () => {
    fetchData();
  },
  { deep: true },
);
</script>
